import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthStore } from '../../auth/state';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { v4 as uuidv4 } from 'uuid';
import './bookmark_page.css';
import { FaCalendarAlt, FaEdit, FaTrash, FaBookReader, FaPencilAlt, FaUndo, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import { MdReviews } from 'react-icons/md';
import interact from 'interactjs';
import cacheManager from '../../auth/cache_manager';

// # Import bookmark icons
import notionIcon from '../../aneo_media/bookmarks/notion.png';
import osmosisIcon from '../../aneo_media/bookmarks/osmosis.png';
import ambossIcon from '../../aneo_media/bookmarks/amboss.png';
import uworldIcon from '../../aneo_media/bookmarks/uworld.png';
import mdcIcon from '../../aneo_media/bookmarks/mdc.png';
import translateIcon from '../../aneo_media/bookmarks/translate.png';
import googleIcon from '../../aneo_media/bookmarks/google.png';
import copilotIcon from '../../aneo_media/bookmarks/copilot.png';
import chatgptIcon from '../../aneo_media/bookmarks/chatgpt.png';
import geminiIcon from '../../aneo_media/bookmarks/gemini.png';
import ecfmgIcon from '../../aneo_media/bookmarks/ecfmg.png';
import facebookIcon from '../../aneo_media/bookmarks/facebook.png';
import instagramIcon from '../../aneo_media/bookmarks/instagram.png';
import twitterIcon from '../../aneo_media/bookmarks/twitter.png';
import tiktokIcon from '../../aneo_media/bookmarks/tiktok.png';
import nbmeIcon from '../../aneo_media/bookmarks/NBME.png';
import bnbIcon from '../../aneo_media/bookmarks/bnb.jpg';

// # Import step icons
import anatomyIcon from '../library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../library/step1/step1_icons/blood.png';
import cnsIcon from '../library/step1/step1_icons/cns.png';
import earIcon from '../library/step1/step1_icons/ear.png';
import emergencyIcon from '../library/step1/step1_icons/emergency.png';
import endocrineIcon from '../library/step1/step1_icons/endocrine.png';
import eyeIcon from '../library/step1/step1_icons/eye.png';
import gitIcon from '../library/step1/step1_icons/git.png';
import healthcareIcon from '../library/step1/step1_icons/healthcare.png';
import heartIcon from '../library/step1/step1_icons/heart.png';
import immunologyIcon from '../library/step1/step1_icons/immunology.png';
import infectiousIcon from '../library/step1/step1_icons/infectious.png';
import kidneyIcon from '../library/step1/step1_icons/kidney.png';
import lungsIcon from '../library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../library/step1/step1_icons/newborn.png';
import pathologyIcon from '../library/step1/step1_icons/pathology.png';
import pharmaIcon from '../library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../library/step1/step1_icons/public_health.png';
import qualityIcon from '../library/step1/step1_icons/quality.png';
import reproductiveIcon from '../library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../library/step1/step1_icons/revision.png';
import skinIcon from '../library/step1/step1_icons/skin.png';
import statisticsIcon from '../library/step1/step1_icons/statistics.png';
import surgeryIcon from '../library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../library/step1/step1_icons/well_patient.png';

function BookmarkPage({ type, isAdmin }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { chapterId } = useParams();
  const [bookmarks, setBookmarks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    url: '',
    description: '',
    chapter: chapterId || '',
    topic: '',
    type: 'For review',
    reviewCount: 0,
    practiceCount: 0,
    schedule: {
      date: '',
      isScheduled: false
    },
    metadata: {
      createdAt: '',
      lastEditedAt: ''
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, bookmarkId: null });
  const [selectedBookmark, setSelectedBookmark] = useState(null);
  const [showFormPopup, setShowFormPopup] = useState(false);

  // # Define quick links for general bookmarks
  const quickLinks = [
    { icon: notionIcon, name: 'Notion', url: 'https://www.notion.so/' },
    { icon: osmosisIcon, name: 'Osmosis', url: 'https://www.osmosis.org/home/dashboard' },
    { icon: ambossIcon, name: 'Amboss', url: 'https://next.amboss.com/us' },
    { icon: uworldIcon, name: 'UWorld', url: 'https://www.uworld.com/app/index.html#/login/' },
    { icon: nbmeIcon, name: 'NBME', url: 'https://www.nbme.org/' },
    { icon: bnbIcon, name: 'Boards & Beyond', url: 'https://www.boardsbeyond.com/login' },
    { icon: mdcIcon, name: 'MDCalc', url: 'https://www.mdcalc.com/' },
    { icon: translateIcon, name: 'Translate', url: 'https://translate.google.com/' },
    { icon: googleIcon, name: 'Google', url: 'https://www.google.com/' },
    { icon: copilotIcon, name: 'Copilot', url: 'https://copilot.microsoft.com/' },
    { icon: chatgptIcon, name: 'ChatGPT', url: 'https://chatgpt.com/' },
    { icon: geminiIcon, name: 'Gemini', url: 'https://gemini.google.com/' },
    { icon: ecfmgIcon, name: 'ECFMG', url: 'https://www.ecfmg.org/' },
    { icon: facebookIcon, name: 'Our Facebook', url: 'https://www.facebook.com/profile.php?id=61563424020612&rdid=GNMtAtY5CO5hpUpQ#' },
    { icon: instagramIcon, name: 'Our Instagram', url: 'https://www.instagram.com/#' },
    { icon: twitterIcon, name: 'Our Twitter', url: 'https://x.com/We_Are_TEMPU?t=hDMPUGfWyB8RiwiN2eaeWg&s=09' },
    { icon: tiktokIcon, name: 'Our TikTok', url: 'https://www.tiktok.com/explore?kuid=3130dfce-c086-437f-9ab0-df56eaf7fd8b&kref=vGWRdzLJnjYf' }
  ];

  // # Get chapters based on step type with icons
  const getChapters = () => {
    if (type === 'step1') {
      return [
        { id: 'Biochemistry', title: 'Biochemistry', icon: biochemistryIcon },
        { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
        { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
        { id: 'General Pathology', title: 'General Pathology', icon: pathologyIcon },
        { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
        { id: 'Public Health', title: 'Public Health Sciences', icon: publicHealthIcon },
        { id: 'Anatomy', title: 'Anatomy & Histology', icon: anatomyIcon },
        { id: 'Cardiovascular', title: 'Cardiovascular System', icon: heartIcon },
        { id: 'Endocrine', title: 'Endocrine System', icon: endocrineIcon },
        { id: 'Gastrointestinal', title: 'Gastrointestinal System', icon: gitIcon },
        { id: 'Hematology', title: 'Hematology & Oncology', icon: bloodIcon },
        { id: 'Musculoskeletal', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
        { id: 'Dermatology', title: 'Dermatology System', icon: skinIcon },
        { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
        { id: 'Special Senses', title: 'Special Senses System', icon: eyeIcon },
        { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
        { id: 'Renal', title: 'Renal System', icon: kidneyIcon },
        { id: 'Reproductive', title: 'Reproductive System', icon: reproductiveIcon },
        { id: 'Respiratory', title: 'Respiratory System', icon: lungsIcon },
        { id: 'Rapid Review', title: 'Rapid Review Series', icon: revisionIcon }
      ];
    } else {
      return [
        { id: 'Cardiovascular', title: 'Cardiovascular', icon: heartIcon },
        { id: 'Respiratory & ENT', title: 'Respiratory & ENT', icon: lungsIcon },
        { id: 'Renal & Urology', title: 'Renal & Urology', icon: kidneyIcon },
        { id: 'Endocrine', title: 'Endocrine', icon: endocrineIcon },
        { id: 'Hematology & Oncology', title: 'Hematology & Oncology', icon: bloodIcon },
        { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
        { id: 'Gastrointestinal', title: 'Gastrointestinal', icon: gitIcon },
        { id: 'Musculoskeletal', title: 'Musculoskeletal', icon: musculoskeletalIcon },
        { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
        { id: 'Ophthalmology', title: 'Ophthalmology', icon: eyeIcon },
        { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
        { id: 'Dermatology', title: 'Clinical Dermatology', icon: skinIcon },
        { id: 'Obstetrics & Gynecology', title: 'Obstetrics & Gynecology', icon: reproductiveIcon },
        { id: 'Pediatrics & Neonatology', title: 'Pediatrics & Neonatology', icon: newbornIcon },
        { id: 'Genetics', title: 'Genetics', icon: biochemistryIcon },
        { id: 'Infectious Disease', title: 'Infectious Disease', icon: infectiousIcon },
        { id: 'Surgery & Anesthesia', title: 'Surgery & Anesthesia', icon: surgeryIcon },
        { id: 'Emergency Medicine & Trauma', title: 'Emergency Medicine & Trauma', icon: emergencyIcon },
        { id: 'Well-Patient', title: 'Well-Patient', icon: wellPatientIcon },
        { id: 'Biostatistics & Epidemiology', title: 'Biostatistics & Epidemiology', icon: statisticsIcon },
        { id: 'Ethics & Communication Skills', title: 'Ethics & Communication', icon: publicHealthIcon },
        { id: 'Healthcare Delivery', title: 'Healthcare Delivery', icon: healthcareIcon },
        { id: 'Quality & Safety', title: 'Quality & Safety', icon: qualityIcon },
        { id: 'Pharmacology Review', title: 'Pharmacology Review', icon: pharmaIcon }
      ];
    }
  };

  // # Get page title
  const getPageTitle = () => {
    if (chapterId) {
      const chapter = getChapters().find(c => c.id === chapterId);
      return chapter ? `${chapter.title} Bookmarks` : 'Chapter Bookmarks';
    }
    switch(type) {
      case 'step1':
        return 'Step 1 Library Bookmarks';
      case 'step2':
        return 'Step 2 Library Bookmarks';
      default:
        return 'General Bookmarks';
    }
  };

  // # Load bookmarks function
  const loadBookmarks = async () => {
    try {
      setLoading(true);
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (type === 'step1') {
          const step1Bookmarks = data.step1Bookmarks || [];
          setBookmarks(
            chapterId 
              ? step1Bookmarks.filter(b => b.chapter === chapterId)
              : step1Bookmarks
          );
        } else if (type === 'step2') {
          const step2Bookmarks = data.step2Bookmarks || [];
          setBookmarks(
            chapterId 
              ? step2Bookmarks.filter(b => b.chapter === chapterId)
              : step2Bookmarks
          );
        } else {
          setBookmarks(data.generalBookmarks || []);
        }
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  // # Load bookmarks on mount and when dependencies change
  useEffect(() => {
    if (user?.uid) {
      loadBookmarks();
    }
  }, [user?.uid, type, chapterId]);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle edit action
  const handleEdit = async (bookmarkId) => {
    const bookmarkToEdit = bookmarks.find(b => b.id === bookmarkId);
    if (bookmarkToEdit) {
      setFormData({
        ...bookmarkToEdit,
        schedule: {
          date: bookmarkToEdit.schedule?.date || '',
          isScheduled: bookmarkToEdit.schedule?.isScheduled || false
        }
      });
      setIsEditing(true);
      setShowFormPopup(true);
    }
  };

  // # Handle form submission
  const handleSubmit = async (formData) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      const timestamp = new Date().toISOString();
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedData = { ...data };
        
        // # Get chapter data if it's a step bookmark
        let chapterData = null;
        if (type !== 'general' && chapterId) {
          chapterData = getChapters().find(c => c.id === chapterId);
        }

        if (isEditing) {
          // # Update existing bookmark
          const bookmarkData = {
            ...formData,
            lastEditedAt: timestamp,
            history: [...(formData.history || []), { action: 'edit', timestamp }]
          };

          if (type === 'general') {
            updatedData.generalBookmarks = data.generalBookmarks.map(b => 
              b.id === formData.id ? bookmarkData : b
            );
          } else if (type === 'step1') {
            updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
              b.id === formData.id ? bookmarkData : b
            );
          } else if (type === 'step2') {
            updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
              b.id === formData.id ? bookmarkData : b
            );
          }
        } else {
          // # Create new bookmark
          const newBookmark = {
            ...formData,
            id: uuidv4(),
            createdAt: timestamp,
            lastEditedAt: timestamp,
            history: [],
            ...(type !== 'general' && {
              step: type,
              chapter: chapterId,
              chapterTitle: chapterData?.title || chapterId,
              topic: formData.topic,
              type: formData.type,
              reviewCount: 0,
              practiceCount: 0,
              schedule: {
                date: formData.schedule?.date || '',
                isScheduled: formData.schedule?.isScheduled || false
              }
            })
          };

          if (type === 'general') {
            updatedData.generalBookmarks = [...(data.generalBookmarks || []), newBookmark];
          } else if (type === 'step1') {
            updatedData.step1Bookmarks = [...(data.step1Bookmarks || []), newBookmark];
          } else if (type === 'step2') {
            updatedData.step2Bookmarks = [...(data.step2Bookmarks || []), newBookmark];
          }
        }

        // # Update metadata
        updatedData.metadata = {
          ...updatedData.metadata,
          bookmarksCount: (
            (updatedData.generalBookmarks?.length || 0) +
            (updatedData.step1Bookmarks?.length || 0) +
            (updatedData.step2Bookmarks?.length || 0)
          ),
          lastEditedAt: timestamp
        };

        await updateDoc(bookmarksRef, updatedData);

        // # Update local state
        if (type === 'general') {
          setBookmarks(updatedData.generalBookmarks);
        } else {
          const stepBookmarks = type === 'step1' ? updatedData.step1Bookmarks : updatedData.step2Bookmarks;
          setBookmarks(chapterId ? 
            stepBookmarks.filter(b => b.chapter === chapterId) : 
            stepBookmarks
          );
        }

        // # Reset form state
        setShowFormPopup(false);
        setIsEditing(false);
        setFormData({
          id: '',
          name: '',
          url: '',
          description: '',
          chapter: chapterId || '',
          topic: '',
          type: 'For review',
          reviewCount: 0,
          practiceCount: 0,
          schedule: {
            date: '',
            isScheduled: false
          },
          history: [],
          metadata: {
            createdAt: '',
            lastEditedAt: ''
          }
        });
      }
    } catch (error) {
      console.error('Error saving bookmark:', error);
    }
  };

  // # Handle review action
  const handleReview = async (bookmarkId) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const timestamp = new Date().toISOString();
        let updatedData = { ...data };
        
        if (type === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              reviewCount: (b.reviewCount || 0) + 1,
              lastEditedAt: timestamp,
              history: [...b.history, { action: 'review', timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step1Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step1Bookmarks
          );
        } else if (type === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              reviewCount: (b.reviewCount || 0) + 1,
              lastEditedAt: timestamp,
              history: [...b.history, { action: 'review', timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step2Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step2Bookmarks
          );
        }

        updatedData.metadata.lastEditedAt = timestamp;
        await updateDoc(bookmarksRef, updatedData);
        setLastAction({ id: bookmarkId, type: 'review' });
      }
    } catch (error) {
      console.error('Error updating review count:', error);
    }
  };

  // # Handle practice action
  const handlePractice = async (bookmarkId) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const timestamp = new Date().toISOString();
        let updatedData = { ...data };
        
        if (type === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              practiceCount: (b.practiceCount || 0) + 1,
              lastEditedAt: timestamp,
              history: [...b.history, { action: 'practice', timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step1Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step1Bookmarks
          );
        } else if (type === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              practiceCount: (b.practiceCount || 0) + 1,
              lastEditedAt: timestamp,
              history: [...b.history, { action: 'practice', timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step2Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step2Bookmarks
          );
        }

        updatedData.metadata.lastEditedAt = timestamp;
        await updateDoc(bookmarksRef, updatedData);
        setLastAction({ id: bookmarkId, type: 'practice' });
      }
    } catch (error) {
      console.error('Error updating practice count:', error);
    }
  };

  // # Handle delete action
  const handleDelete = (bookmarkId) => {
    const bookmarkToDelete = bookmarks.find(b => b.id === bookmarkId);
    setDeleteConfirmation({
      show: true,
      bookmarkId,
      bookmarkData: bookmarkToDelete
    });
  };

  // # Confirmation Dialog Component
  const ConfirmationDialog = ({ onConfirm, onCancel, bookmarkData }) => (
    <div className="bmk_pg_dialog-overlay">
      <div className="bmk_pg_confirmation-dialog">
        <h3>Delete Bookmark</h3>
        <p>Are you sure you want to delete this bookmark?</p>
        <div className="bmk_pg_dialog-buttons">
          <button className="bmk_pg_cancel-delete" onClick={onCancel}>
            Cancel
          </button>
          <button className="bmk_pg_confirm-delete" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // # Add actual delete function
  const confirmDelete = async (bookmarkId) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const timestamp = new Date().toISOString();
        let updatedData = { ...data };
        
        if (type === 'general') {
          updatedData.generalBookmarks = data.generalBookmarks.filter(b => b.id !== bookmarkId);
          setBookmarks(updatedData.generalBookmarks);
        } else if (type === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.filter(b => b.id !== bookmarkId);
          setBookmarks(chapterId ? 
            updatedData.step1Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step1Bookmarks
          );
        } else if (type === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.filter(b => b.id !== bookmarkId);
          setBookmarks(chapterId ? 
            updatedData.step2Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step2Bookmarks
          );
        }

        updatedData.metadata = {
          ...updatedData.metadata,
          lastEditedAt: timestamp,
          bookmarksCount: (
            (updatedData.generalBookmarks?.length || 0) +
            (updatedData.step1Bookmarks?.length || 0) +
            (updatedData.step2Bookmarks?.length || 0)
          )
        };

        await updateDoc(bookmarksRef, updatedData);
        setDeleteConfirmation({ show: false, bookmarkId: null, bookmarkData: null });
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  // # Handle undo action
  const handleUndo = async (bookmarkId, actionType) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const timestamp = new Date().toISOString();
        let updatedData = { ...data };
        
        if (type === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              [actionType === 'review' ? 'reviewCount' : 'practiceCount']: 
                Math.max(0, (b[actionType === 'review' ? 'reviewCount' : 'practiceCount'] || 0) - 1),
              lastEditedAt: timestamp,
              history: [...b.history, { action: `undo_${actionType}`, timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step1Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step1Bookmarks
          );
        } else if (type === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
            b.id === bookmarkId ? {
              ...b,
              [actionType === 'review' ? 'reviewCount' : 'practiceCount']: 
                Math.max(0, (b[actionType === 'review' ? 'reviewCount' : 'practiceCount'] || 0) - 1),
              lastEditedAt: timestamp,
              history: [...b.history, { action: `undo_${actionType}`, timestamp }]
            } : b
          );
          setBookmarks(chapterId ? 
            updatedData.step2Bookmarks.filter(b => b.chapter === chapterId) : 
            updatedData.step2Bookmarks
          );
        }

        updatedData.metadata.lastEditedAt = timestamp;
        await updateDoc(bookmarksRef, updatedData);
        setLastAction(null);
      }
    } catch (error) {
      console.error('Error undoing action:', error);
    }
  };

  // # Helper function to check if text needs "Read More"
  const needsReadMore = (text) => {
    if (!text) return false;
    return text.split(' ').length > 5 || text.length > 30;
  };

  // # Popup component
  const BookmarkPopup = ({ bookmark, onClose }) => (
    <div className="bmk_pg_bookmark-popup-overlay" onClick={onClose}>
      <div className="bmk_pg_bookmark-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="bmk_pg_bookmark-popup-close" onClick={onClose}>
          <FaTimes />
        </button>
        <h3>{bookmark.name}</h3>
        <p>{bookmark.description}</p>
        <a 
          href={bookmark.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="bmk_pg_add-bookmark-btn"
          style={{ display: 'inline-block', textDecoration: 'none' }}
        >
          Visit Link
        </a>
      </div>
    </div>
  );

  // # Form Popup Component
  const FormPopup = ({ isEdit, onSubmit, onClose, initialData }) => {
    const [localFormData, setLocalFormData] = useState(initialData);

    const handleLocalScheduleChange = (e) => {
      const isChecked = e.target.checked;
      setLocalFormData(prev => ({
        ...prev,
        schedule: {
          date: isChecked ? new Date().toISOString().split('T')[0] : '',
          isScheduled: isChecked
        }
      }));
    };

    return (
      <div className="bmk_pg_form-popup-overlay" onClick={onClose}>
        <div className="bmk_pg_form-popup-content" onClick={(e) => e.stopPropagation()}>
          <button className="bmk_pg_form-popup-close" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>{isEdit ? "Edit Bookmark" : "Add New Bookmark"}</h2>
          <form className="bmk_pg_add-bookmark-form" onSubmit={(e) => {
            e.preventDefault();
            onSubmit(localFormData);
          }}>
            {type !== 'general' && (
              <select
                value={localFormData.type}
                onChange={(e) => setLocalFormData({ ...localFormData, type: e.target.value })}
                required
              >
                <option value="For review">For Review</option>
                <option value="For practice">For Practice</option>
                <option value="Hard">Hard Topic</option>
              </select>
            )}
            <input
              type="text"
              placeholder={type === 'general' ? "Bookmark Name" : "Topic Name"}
              value={localFormData.name}
              onChange={(e) => setLocalFormData({ ...localFormData, name: e.target.value })}
              required
            />
            {type === 'general' && (
              <input
                type="url"
                placeholder="URL"
                value={localFormData.url}
                onChange={(e) => setLocalFormData({ ...localFormData, url: e.target.value })}
                required
              />
            )}
            <textarea
              placeholder="Description (optional)"
              value={localFormData.description}
              onChange={(e) => setLocalFormData({ ...localFormData, description: e.target.value })}
            />
            <div className="bmk_pg_schedule-group">
              <label>
                <input
                  type="checkbox"
                  checked={localFormData.schedule?.isScheduled || false}
                  onChange={handleLocalScheduleChange}
                />
                Schedule this bookmark
              </label>
              {localFormData.schedule?.isScheduled && (
                <input
                  type="date"
                  value={localFormData.schedule?.date || ''}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      date: e.target.value
                    }
                  }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              )}
            </div>
            <div className="bmk_pg_form-buttons">
              <button type="submit">
                {isEdit ? "Save Changes" : "Add Bookmark"}
              </button>
              <button type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // # Render chapter grid for step1 and step2 bookmarks
  const renderChapterGrid = () => (
    <div className="bmk_pg_chp_chapters-grid">
      {getChapters().map((chapter) => (
        <div
          key={chapter.id}
          className="bmk_pg_chp_chapter-card"
          onClick={() => navigate(isAdmin ? `/admin/bookmarks/${type}/${chapter.id}` : `/bookmarks/${type}/${chapter.id}`)}
        >
          <div className="bmk_pg_chp_chapter-icon">
            <img src={chapter.icon} alt={chapter.title} />
          </div>
          <h3>{chapter.title}</h3>
          <p>View Bookmarks</p>
        </div>
      ))}
    </div>
  );

  // # Render general bookmarks page
  const renderGeneralBookmarks = () => (
          <>
            {/* Quick Links Section */}
            <section className="bmk_pg_quick-links-section">
              <h2>Quick Links</h2>
              <div className="bmk_pg_quick-links-grid">
                {quickLinks.map((link) => (
                  <a 
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bmk_pg_quick-link-card"
                  >
                    <img src={link.icon} alt={link.name} />
                    <span>{link.name}</span>
                  </a>
                ))}
              </div>
            </section>

            {/* My Bookmarks Section */}
            <section className="bmk_pg_my-bookmarks-section">
              <div className="bmk_pg_section-header bmk_pg_centered">
                <h2>My Bookmarks</h2>
                <button onClick={() => {
                  setFormData({
                    id: '',
                    name: '',
                    url: '',
                    description: '',
                    chapter: '',
                    topic: '',
                    type: 'For review',
                    reviewCount: 0,
                    practiceCount: 0,
                    schedule: {
                      date: '',
                      isScheduled: false
                    },
                    history: [],
                    metadata: {
                      createdAt: '',
                      lastEditedAt: ''
                    }
                  });
                  setIsEditing(false);
                  setShowFormPopup(true);
                }} className="bmk_pg_add-bookmark-btn">
                  Add Bookmark
                </button>
              </div>

              <div className="bmk_pg_bookmarks-grid">
                {bookmarks.filter(bookmark => 
                  !quickLinks.some(link => 
                    link.name === bookmark.name || 
                    (bookmark.name.includes('Our') && 
                     ['Facebook', 'Instagram', 'Twitter', 'TikTok'].some(social => 
                       bookmark.name.includes(social)
                     ))
                  )
                ).map((bookmark) => (
                  <div key={bookmark.id} className="bmk_pg_bookmark-card">
                    <div className="bmk_pg_bookmark-header">
                      <h3>{bookmark.name}</h3>
                      <p>{bookmark.description}</p>
                    </div>
                    <div className="bmk_pg_bookmark-actions">
                      <a 
                        href={bookmark.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bmk_pg_bookmark-action-button"
                        title="Visit link"
                      >
                        <FaExternalLinkAlt />
                      </a>
                      {(needsReadMore(bookmark.name) || needsReadMore(bookmark.description)) && (
                        <button 
                          className="bmk_pg_bookmark-action-button"
                          onClick={() => setSelectedBookmark(bookmark)}
                          title="Read more"
                        >
                          <FaBookReader />
                        </button>
                      )}
                      <button 
                        onClick={() => handleEdit(bookmark.id)}
                        className="bmk_pg_bookmark-action-button bmk_pg_bookmark-edit-button"
                        title="Edit bookmark"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(bookmark.id)}
                        className="bmk_pg_bookmark-action-button bmk_pg_bookmark-delete-button"
                        title="Delete bookmark"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {selectedBookmark && (
                <BookmarkPopup 
                  bookmark={selectedBookmark} 
                  onClose={() => setSelectedBookmark(null)} 
                />
              )}

              {showFormPopup && (
                <FormPopup
                  isEdit={isEditing}
                  onSubmit={handleSubmit}
                  onClose={() => {
                    setShowFormPopup(false);
                    setIsEditing(false);
                    setFormData({
                      id: '',
                      name: '',
                      url: '',
                      description: '',
                      chapter: '',
                      topic: '',
                      type: 'For review',
                      reviewCount: 0,
                      practiceCount: 0,
                      schedule: {
                        date: '',
                        isScheduled: false
                      },
                      history: [],
                      metadata: {
                        createdAt: '',
                        lastEditedAt: ''
                      }
                    });
                  }}
                  initialData={formData}
                />
              )}

              {deleteConfirmation.show && (
                <ConfirmationDialog
                  onConfirm={() => confirmDelete(deleteConfirmation.bookmarkId)}
                  onCancel={() => setDeleteConfirmation({ show: false, bookmarkId: null, bookmarkData: null })}
                  bookmarkData={deleteConfirmation.bookmarkData}
                />
              )}
            </section>
          </>
  );

  // # Render step bookmarks page
  const renderStepBookmarks = () => (
    <section className="bmk_pg_step-bookmarks-section">
      <div className="bmk_pg_section-header">
        <h2>{getPageTitle()}</h2>
        {chapterId && (
          <button onClick={() => {
            setFormData({
              id: '',
              name: '',
              url: '',
              description: '',
              chapter: chapterId || '',
              topic: '',
              type: 'For review',
              reviewCount: 0,
              practiceCount: 0,
              schedule: {
                date: '',
                isScheduled: false
              },
              history: [],
              metadata: {
                createdAt: '',
                lastEditedAt: ''
              }
            });
            setIsEditing(false);
            setShowFormPopup(true);
          }} className="bmk_pg_add-bookmark-btn">
            Add Bookmark
          </button>
        )}
      </div>

      {!chapterId ? (
        renderChapterGrid()
      ) : (
        <div className="bmk_pg_bookmarks-grid">
          {bookmarks
            .filter(b => !chapterId || b.chapter === chapterId)
            .map((bookmark) => (
              <div key={bookmark.id} className="bmk_pg_chp_step-bookmark-card">
                <div className="bmk_pg_chp_bookmark-header">
                  <h3>{bookmark.name}</h3>
                  <div className="bmk_pg_chp_bookmark-header-actions">
                    <span className={`bmk_pg_chp_type-badge ${bookmark.type.toLowerCase().replace(' ', '-')}`}>
                      {bookmark.type}
                    </span>
                  </div>
                </div>
                <p className="bmk_pg_chp_chapter-name">{bookmark.chapter}</p>
                {bookmark.description && (
                  <p className="bmk_pg_chp_description">{bookmark.description}</p>
                )}
                <div className="bmk_pg_chp_bookmark-stats">
                  <span>
                    <FaBookReader /> Review: {bookmark.reviewCount}
                  </span>
                  <span>
                    <FaPencilAlt /> Practice: {bookmark.practiceCount}
                  </span>
                </div>
                {bookmark.schedule?.isScheduled && (
                  <div className="bmk_pg_chp_schedule-info">
                    <FaCalendarAlt />
                    Scheduled: {new Date(bookmark.schedule.date).toLocaleDateString()}
                  </div>
                )}
                <div className="bmk_pg_chp_bookmark-actions">
                  <button onClick={() => handleReview(bookmark.id)}>
                    <FaBookReader /> Review
                  </button>
                  <button onClick={() => handlePractice(bookmark.id)}>
                    <FaPencilAlt /> Practice
                  </button>
                  <button onClick={() => handleEdit(bookmark.id)}>
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => handleDelete(bookmark.id)}>
                    <FaTrash /> Delete
                  </button>
                </div>
                {lastAction?.id === bookmark.id && (
                  <button 
                    className="bmk_pg_chp_undo-button"
                    onClick={() => handleUndo(bookmark.id, lastAction.type)}
                    title="Undo last action"
                  >
                    <FaUndo />
                  </button>
                )}
              </div>
            ))}
        </div>
      )}
    </section>
  );

  // # Handle scheduling
  const handleSchedule = async (bookmarkId, scheduleDate) => {
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        let updatedData = { ...data };
        const timestamp = new Date().toISOString();

        // # Update the bookmark's schedule based on its type
        if (type === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
            b.id === bookmarkId 
              ? { 
                  ...b, 
                  schedule: { 
                    isScheduled: true, 
                    date: scheduleDate.toISOString() 
                  } 
                } 
              : b
          );
        } else if (type === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
            b.id === bookmarkId 
              ? { 
                  ...b, 
                  schedule: { 
                    isScheduled: true, 
                    date: scheduleDate.toISOString() 
                  } 
                } 
              : b
          );
        } else {
          updatedData.generalBookmarks = data.generalBookmarks.map(b => 
            b.id === bookmarkId 
              ? { 
                  ...b, 
                  schedule: { 
                    isScheduled: true, 
                    date: scheduleDate.toISOString() 
                  } 
                } 
              : b
          );
        }

        // # Update metadata
        updatedData.metadata = {
          ...updatedData.metadata,
          lastEditedAt: timestamp
        };

        await updateDoc(bookmarksRef, updatedData);
        
        // # Refresh bookmarks
        await loadBookmarks();
      }
    } catch (error) {
      console.error('Error scheduling bookmark:', error);
    }
  };

  // # Initialize drag and drop for scheduling
  useEffect(() => {
    if (!type) return;

    // # Make bookmarks draggable
    interact('.bmk_pg_bookmark-card, .bmk_pg_step-bookmark-card').draggable({
      inertia: true,
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: 'parent',
          endOnly: true
        })
      ],
      autoScroll: true,
      listeners: {
        start(event) {
          const bookmarkId = event.target.getAttribute('data-bookmark-id');
          event.target.classList.add('bmk_pg_dragging');
        },
        end(event) {
          event.target.classList.remove('bmk_pg_dragging');
        }
      }
    });

    // # Make schedule button droppable
    interact('.bmk_pg_schedule-button').dropzone({
      accept: '.bmk_pg_bookmark-card, .bmk_pg_step-bookmark-card',
      overlap: 0.75,
      ondropactivate: function (event) {
        event.target.classList.add('bmk_pg_drop-active');
      },
      ondragenter: function (event) {
        event.target.classList.add('bmk_pg_drop-target');
      },
      ondragleave: function (event) {
        event.target.classList.remove('bmk_pg_drop-target');
      },
      ondrop: function (event) {
        const bookmarkId = event.relatedTarget.getAttribute('data-bookmark-id');
        if (bookmarkId) {
          // # Show date picker dialog
          const scheduleDate = new Date();
          handleSchedule(bookmarkId, scheduleDate);
        }
      },
      ondropdeactivate: function (event) {
        event.target.classList.remove('bmk_pg_drop-active');
        event.target.classList.remove('bmk_pg_drop-target');
      }
    });

    return () => {
      interact('.bmk_pg_bookmark-card, .bmk_pg_step-bookmark-card').unset();
      interact('.bmk_pg_schedule-button').unset();
    };
  }, [type]);

  // # Modify bookmark card rendering to include data-bookmark-id
  const renderBookmarkCard = (bookmark) => (
    <div className="bmk_pg_bookmark-card" data-bookmark-id={bookmark.id}>
      {/* ... existing bookmark card content ... */}
    </div>
  );

  // # Add schedule button to the header
  const renderHeader = () => (
    <div className="bmk_pg_bookmarks-header">
      <h1>{getPageTitle()}</h1>
      <div className="bmk_pg_bookmarks-actions">
        <button 
          className="bmk_pg_schedule-button"
          onClick={() => navigate(isAdmin ? '/admin/bookmarks/calendar' : '/bookmarks/calendar')}
        >
          <FaCalendarAlt />
          <span>View Schedule</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className={`bmk_pg_bookmarks-container ${isDarkMode ? 'bmk_pg_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="bmk_pg_bookmarks-main-content">
        <div className="bmk_pg_bookmarks-page-title">
          {renderHeader()}
        </div>

        {loading ? (
          <div className="bmk_pg_bookmarks-loading">Loading bookmarks...</div>
        ) : type === 'general' ? (
          renderGeneralBookmarks()
        ) : (
          <>
            {deleteConfirmation.show && (
              <ConfirmationDialog
                onConfirm={() => confirmDelete(deleteConfirmation.bookmarkId)}
                onCancel={() => setDeleteConfirmation({ show: false, bookmarkId: null, bookmarkData: null })}
                bookmarkData={deleteConfirmation.bookmarkData}
              />
            )}
            {showFormPopup && (
              <FormPopup
                isEdit={isEditing}
                onSubmit={handleSubmit}
                onClose={() => {
                  setShowFormPopup(false);
                  setIsEditing(false);
                  setFormData({
                    id: '',
                    name: '',
                    url: '',
                    description: '',
                    chapter: chapterId || '',
                    topic: '',
                    type: 'For review',
                    reviewCount: 0,
                    practiceCount: 0,
                    schedule: {
                      date: '',
                      isScheduled: false
                    },
                    history: [],
                    metadata: {
                      createdAt: '',
                      lastEditedAt: ''
                    }
                  });
                }}
                initialData={formData}
              />
            )}
            {renderStepBookmarks()}
          </>
        )}
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default BookmarkPage; 